/**
 * SpeechRecognitionContext.js
 * React Context for sharing speech recognition state across Constellation KG
 * 
 * This context wraps the useSpeechRecognition hook and provides its state
 * and methods to any component in the app. This allows:
 * 
 * - Sidebar to control the master enable/disable toggle
 * - AI Explorer and My Journal to share the same recognition instance
 * - Consistent permission state across all components
 * 
 * Usage:
 * 
 * 1. Wrap your app with the provider (in App.js):
 *    
 *    import { SpeechRecognitionProvider } from './contexts/SpeechRecognitionContext';
 *    
 *    function App() {
 *      return (
 *        <SpeechRecognitionProvider>
 *          <YourAppContent />
 *        </SpeechRecognitionProvider>
 *      );
 *    }
 * 
 * 2. Use the context in any component:
 *    
 *    import { useSpeechRecognitionContext } from '../contexts/SpeechRecognitionContext';
 *    
 *    function MyComponent() {
 *      const { isRecording, toggleRecording, isEnabled } = useSpeechRecognitionContext();
 *      // ...
 *    }
 */

import React, { createContext, useContext, useMemo } from 'react';
import useSpeechRecognition from '../hooks/useSpeechRecognition';

// Import constants separately to avoid circular dependency issues
export {
  PERMISSION_STATES,
  STATUS_TYPES,
  ON_DEVICE_STATES,
  DICTATION_REFUSED_MESSAGE,
  DICTATION_RETRY_MESSAGE,
  DICTATION_PERMISSION_MESSAGE,
  DICTATION_PREPARING_MESSAGE
} from '../hooks/useSpeechRecognition';

// =========================================
// Context Creation
// =========================================

/**
 * The context object
 * Default value is null - we'll throw an error if used outside provider
 */
const SpeechRecognitionContext = createContext(null);

// =========================================
// Provider Component
// =========================================

/**
 * SpeechRecognitionProvider
 * 
 * Wraps the app and provides speech recognition functionality to all children.
 * Should be placed near the top of your component tree, typically in App.js.
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 */
export const SpeechRecognitionProvider = ({ children }) => {
  // Initialize the speech recognition hook
  const speechRecognition = useSpeechRecognition();
  
  // Memoize the context value to prevent unnecessary re-renders
  // Only updates when the speech recognition state actually changes
  const contextValue = useMemo(() => ({
    // State
    isRecording: speechRecognition.isRecording,
    isEnabled: speechRecognition.isEnabled,
    isSupported: speechRecognition.isSupported,
    isOnDeviceRecognition: speechRecognition.isOnDeviceRecognition,
    // The on-device verdict and the two booleans derived from it. Added
    // October 12, 2026 with the dictation ruling: the microphone control needs
    // to render disabled-with-a-reason, which it cannot do from isSupported
    // alone — a browser can support the Web Speech API and still be unable to
    // recognize speech on the device.
    onDeviceState: speechRecognition.onDeviceState,
    isDictationAvailable: speechRecognition.isDictationAvailable,
    isDictationRefused: speechRecognition.isDictationRefused,
    permissionState: speechRecognition.permissionState,
    statusType: speechRecognition.statusType,
    statusMessage: speechRecognition.statusMessage,
    
    // Actions
    startRecording: speechRecognition.startRecording,
    stopRecording: speechRecognition.stopRecording,
    toggleRecording: speechRecognition.toggleRecording,
    toggleEnabled: speechRecognition.toggleEnabled,
    setEnabled: speechRecognition.setEnabled,
    requestPermission: speechRecognition.requestPermission,
    clearStatus: speechRecognition.clearStatus
  }), [
    speechRecognition.isRecording,
    speechRecognition.isEnabled,
    speechRecognition.isSupported,
    speechRecognition.isOnDeviceRecognition,
    speechRecognition.onDeviceState,
    speechRecognition.isDictationAvailable,
    speechRecognition.isDictationRefused,
    speechRecognition.permissionState,
    speechRecognition.statusType,
    speechRecognition.statusMessage,
    speechRecognition.startRecording,
    speechRecognition.stopRecording,
    speechRecognition.toggleRecording,
    speechRecognition.toggleEnabled,
    speechRecognition.setEnabled,
    speechRecognition.requestPermission,
    speechRecognition.clearStatus
  ]);
  
  return (
    <SpeechRecognitionContext.Provider value={contextValue}>
      {children}
    </SpeechRecognitionContext.Provider>
  );
};

// =========================================
// Consumer Hook
// =========================================

/**
 * useSpeechRecognitionContext
 * 
 * Custom hook to access the speech recognition context.
 * Must be used within a SpeechRecognitionProvider.
 * 
 * @returns {Object} Speech recognition state and methods
 * @throws {Error} If used outside of SpeechRecognitionProvider
 * 
 * @example
 * function MicrophoneButton() {
 *   const { isRecording, toggleRecording, isEnabled } = useSpeechRecognitionContext();
 *   
 *   if (!isEnabled) return null;
 *   
 *   return (
 *     <button onClick={() => toggleRecording(textareaRef, maxLength)}>
 *       {isRecording ? 'Stop' : 'Start'}
 *     </button>
 *   );
 * }
 */
export const useSpeechRecognitionContext = () => {
  const context = useContext(SpeechRecognitionContext);
  
  if (context === null) {
    throw new Error(
      'useSpeechRecognitionContext must be used within a SpeechRecognitionProvider. ' +
      'Wrap your app with <SpeechRecognitionProvider> in App.js.'
    );
  }
  
  return context;
};

// =========================================
// Export the context itself (for advanced use cases)
// =========================================

export default SpeechRecognitionContext;
